// adapted from
// https://decembersoft.com/posts/file-upload-progress-with-redux-saga/

import { buffers, channel, END } from 'redux-saga';

type ChannelContents =
  | {
      error: Error;
      success?: undefined;
      progress?: undefined;
      file: File;
      descriptor: UploadDescriptor;
    }
  | {
      error?: undefined;
      success: boolean;
      progress?: undefined;
      file: File;
      descriptor: UploadDescriptor;
    }
  | {
      error?: undefined;
      success?: undefined;
      progress: number;
      file: File;
      descriptor: UploadDescriptor;
    };

export interface UploadDescriptor {
  url: string;
  key: string;
  bucket: string;
  expiresInSeconds: number;
  getUrl: string;
  unsignedUrl: string;
  type?: string;
}

export default function uploadFilesChannel(
  uploadDescriptors: UploadDescriptor[],
  files: File[],
  method = 'PUT'
) {
  if (uploadDescriptors.length !== files.length) {
    throw new Error('Need an upload descriptor for each file to be uploaded!');
  }

  /**
   * EXPANDING, not sliding, and that is a correctness requirement.
   *
   * This channel carries three kinds of message over one wire: per-file
   * `progress`, the terminal `success`/`error` for each file, and the final
   * `END`. The consumer in `filePicker/saga.ts` loops
   * `while (result.length !== files.length)`, so it needs every terminal
   * message to arrive.
   *
   * A sliding buffer DROPS the oldest message once it is full, and this one was
   * `sliding(2)`. Whenever more than two messages landed between two takes —
   * the saga dispatches to Redux between them, which re-renders — the oldest
   * was discarded. Lose one `success` and `result.length` never reaches
   * `files.length`; `remainingFiles` still hits zero, `END` is put, and
   * redux-saga terminates the task silently on a take from a channel that has
   * ended. `finishUpload` never runs: the dialog hangs with no error, no
   * snackbar and no callback. At five files that was a narrow race. At the
   * per-type caps it is six times the terminal traffic through the same two
   * slots.
   *
   * Nothing may be dropped, so nothing is. Progress volume is held down at the
   * producer instead — see `onFileProgress`.
   */
  const chan = channel(buffers.expanding<ChannelContents>());
  let remainingFiles = files.length;

  /**
   * Last whole percentage point reported per file, so `onFileProgress` can skip
   * the puts that would say nothing new. Indexed like `files`.
   */
  const lastReportedPercent: number[] = new Array<number>(files.length).fill(
    -1
  );

  const onFileProgress = (
    index: number,
    file: File,
    descriptor: UploadDescriptor,
    {
      lengthComputable,
      loaded,
      total,
    }: { lengthComputable: boolean; loaded: number; total: number }
  ) => {
    if (lengthComputable) {
      // todo: if multiple files with drastically different sizes are
      // uploaded at the same time, the mean percentage isn't a good
      // representation of remaining time.
      // ie. given files of size [1, 1, 10], with progress [100, 100, 20]%,
      // mean(100, 100, 20) ~= 75%, but the true remaining time should be
      // calculated on remaining/total size (1+1+2) / (1+1+10) = 33%
      const progress = (loaded / total) * 100;
      // Throttled to whole percentage points, per file. The buffer above no
      // longer discards anything, so this is where progress volume is kept in
      // check — every put costs the consumer a Redux dispatch, and a `progress`
      // that rounds to the same number the UI is already showing buys nothing.
      // Terminal messages are never throttled.
      const whole = Math.floor(progress);
      if (whole === lastReportedPercent[index]) {
        return;
      }
      lastReportedPercent[index] = whole;
      chan.put({ progress, file, descriptor });
    }
  };

  /**
   * Whether each file has already put its terminal message. Indexed like
   * `files`.
   *
   * A network error mid-upload reaches BOTH terminal paths below: the XHR
   * request-error steps move to DONE (status 0) and fire `readystatechange`,
   * then fire `error` on `xhr.upload`. Without this guard that file puts two
   * terminal messages, and because the consumer counts terminals until
   * `result.length === files.length`, it finishes one file early — while
   * another upload is still in flight, which then goes missing from the result
   * and the failed file is reported twice. The sliding buffer this channel used
   * to have hid that some of the time by dropping messages; the expanding one
   * delivers every put, so each file must put exactly one terminal.
   */
  const settled: boolean[] = new Array<boolean>(files.length).fill(false);

  const settle = (index: number, message: ChannelContents) => {
    if (settled[index]) {
      return;
    }
    settled[index] = true;
    remainingFiles -= 1;
    chan.put(message);

    if (remainingFiles === 0) {
      chan.put(END);
    }
  };

  const onXHRError = (
    index: number,
    file: File,
    descriptor: UploadDescriptor
  ) => {
    settle(index, { error: new Error('File upload error'), file, descriptor });
  };

  const onFileReadyStateChange = (
    index: number,
    file: File,
    descriptor: UploadDescriptor,
    { readyState, status }: XMLHttpRequest
  ) => {
    if (readyState === XMLHttpRequest.DONE) {
      settle(
        index,
        status >= 200 && status <= 300
          ? { success: true, file, descriptor }
          : { error: new Error('Upload failed'), file, descriptor }
      );
    }
  };

  files.forEach((file, i) => {
    const descriptor = uploadDescriptors[i]!; // Safe due to previous length check
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener(
      'progress',
      onFileProgress.bind(null, i, file, descriptor)
    );
    xhr.upload.addEventListener(
      'error',
      onXHRError.bind(null, i, file, descriptor),
      { once: true }
    );
    xhr.onreadystatechange = onFileReadyStateChange.bind(
      null,
      i,
      file,
      descriptor,
      xhr
    );
    // xhr.upload.addEventListener('abort', onFailure.bind(null, file, descriptor));
    xhr.open(method, descriptor.url, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    if (file.type === '' && file.name.endsWith('.cme')) {
      descriptor.type = 'video/mp4';
      xhr.setRequestHeader('Content-Type', 'video/mp4');
    }
    xhr.send(file);
  });

  return chan;
}
